import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useApplicationIndustries } from '@/api/methods/useApplicationIndustries';
import { trackCtaClick } from '@/lib/trackCtaClick.ts';
import { scrollToApplyForm } from '@/lib/scroll';
import { useApplicationContext } from '@/contexts/ApplicationContext';
import { useIndustryNameGetter } from '@/hooks/useIndustryNameGetter.ts';
import { TestIds } from '@/constants/testIds';

interface JobCardProps {
  title: string;
  imageUrl: string;
  imageAlt: string;
  clientId: number | string | undefined;
}

const IndustryCard = ({
  title,
  imageUrl,
  imageAlt,
  clientId,
}: JobCardProps) => {
  const { setIndustryIdToApply } = useApplicationContext();

  const handleIndustryClick = () => {
    if (clientId) {
      setIndustryIdToApply(clientId.toString());
    }

    trackCtaClick('more_companies');
    scrollToApplyForm();
  };

  return (
    <div
      data-testid={
        clientId ? TestIds.getHired.industryCard(clientId) : undefined
      }
      className="p-2 rounded-lg bg-muted overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
      onClick={handleIndustryClick}
    >
      {/* Image */}
      <div className="w-full aspect-square bg-muted rounded-lg overflow-hidden">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
      </div>
      {/* Title */}
      <div className="p-2 pb-0 text-center">
        <h3 className="text-foreground">{title}</h3>
      </div>
    </div>
  );
};

export const GetHired = () => {
  const { t } = useTranslation();
  const { data: industries } = useApplicationIndustries();
  const getIndustryName = useIndustryNameGetter();

  if (!industries?.length) {
    return null;
  }

  const handleMoreButtonClick = () => {
    trackCtaClick('more_companies');
    scrollToApplyForm();
  };

  return (
    <div className="w-full p-6 md:p-8 mb-8">
      {/* Title */}
      <h2
        data-testid={TestIds.getHired.title}
        className="text-2xl md:text-3xl font-bold text-center mb-6"
      >
        {t('landing.getHired.title')}
      </h2>

      {/* Job Cards Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {industries.map((industry) => (
          <IndustryCard
            key={industry.client_id}
            clientId={industry.client_id}
            title={getIndustryName(industry)}
            imageUrl={industry.image_url as string}
            imageAlt={getIndustryName(industry)}
          />
        ))}
      </div>

      {/* More Roles Button */}
      <Button
        data-testid={TestIds.getHired.moreCompanies}
        className="w-full bg-green-50 border-[1px] border-primary text-primary hover:bg-green-100 rounded-lg py-3 font-medium"
        onClick={handleMoreButtonClick}
      >
        <Plus className="w-4 h-4" />
        {t('landing.getHired.moreCompanies')}
      </Button>
    </div>
  );
};
